/**
 * Is this the network dropping, rather than the page being broken?
 *
 * ── WHY THIS IS ITS OWN FILE ────────────────────────────────────────────────
 *
 * Because there are three boundaries that need the answer and exactly one of
 * them is allowed to be wrong. `BoundaryErrorState` (every segment boundary),
 * `src/app/error.tsx` (root) and `src/app/global-error.tsx` all use this
 * verdict to decide whether to re-render once in silence or replace the screen
 * the owner is standing on. Three copies of a regex is three answers to one
 * question, and that is how exactly one of them stays wrong with nobody
 * noticing — same failure family as the provider `isCompany` rule.
 *
 * ── WHY IT HAS NO IMPORTS, AND MUST NEVER GAIN ANY ──────────────────────────
 *
 * The root and global boundaries are deliberately dependency-light: one of the
 * errors they catch is a ChunkLoadError, and a recovery screen that must load
 * a chunk to render is no recovery screen at all. They inline their own Sentry
 * capture rather than share a component for exactly that reason. A pure,
 * import-free predicate is a different kind of dependency from a UI component
 * — it carries no graph behind it — but only for as long as it stays that way.
 * `every-boundary-retries-a-dropped-connection.test.tsx` fails if this file
 * ever grows an `import`.
 *
 * ── WHY THE CHECK IS NARROW ─────────────────────────────────────────────────
 *
 * WebKit words a failed fetch "Load failed" and Chrome "Failed to fetch"; both
 * arrive as a bare TypeError with no status and no stack worth reading. A
 * false positive here spends one extra render hiding a real defect, so the
 * pattern stays tight rather than catching anything that mentions a network.
 */
export function isTransientNetworkError(error: { message?: string } | null | undefined): boolean {
  const message = error?.message || '';
  return /load failed|failed to fetch|network ?error|networkerror/i.test(message);
}
